/* Immagini, GIF e video senza dover incollare link: si caricano dal PC (o da Tenor/Giphy) e il sito li tiene online. */
import { useRef, useState } from "react";
import { toast } from "sonner";
import { Film, Loader2, Paperclip, Upload, X } from "lucide-react";
import { apiError, http } from "@/lib/api";
import { Input } from "@/components/ui/input";

const PAGE_LINK = /^https?:\/\/(www\.)?(tenor\.com|tenor\.co|giphy\.com)\//i;
const IMAGE_TYPES = "image/gif,image/png,image/jpeg,image/webp";
const VIDEO_TYPES = "video/mp4,video/webm,video/quicktime";
const MAX = 25 * 1024 * 1024;
const inputC = "border-[#232838] bg-[#131620] text-white placeholder:text-[#5f6479] focus-visible:ring-[#5865F2]";
export const isVideo = (url) => /\.(mp4|webm|mov)($|\?)/i.test(url || "");

/** Carica un file sul sito e restituisce il link pubblico (https://dominio/media/…). */
export function uploadMedia(file) {
  return new Promise((resolve, reject) => {
    if (file.size > MAX) return reject(new Error("File troppo grande (massimo 25 MB)"));
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("File non leggibile"));
    reader.onload = async () => {
      try {
        const { data } = await http.post("/media/upload", { data: reader.result });
        resolve(`${window.location.origin}${data.path}`);
      } catch (e) { reject(new Error(apiError(e))); }
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Campo per un'immagine dell'embed: link, pagina Tenor/Giphy oppure file dal PC.
 * I video non possono stare dentro un embed (limite di Discord): se ne carichi uno, il link va passato a onVideo.
 */
export function MediaInput({ value, onChange, placeholder = "Carica dal PC o incolla un link (anche Tenor / Giphy)", onVideo, compact = false }) {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const resolve = async (url) => {
    if (!PAGE_LINK.test(url || "")) return;
    setBusy(true);
    try {
      const { data } = await http.post("/media/resolve", { url });
      if (data?.url && data.url !== url) { onChange(data.url); toast.success("GIF trovata", { description: "Ho messo il link diretto della GIF." }); }
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    const video = file.type.startsWith("video/");
    if (video && !onVideo) {
      toast.error("I video non si possono mettere dentro un embed", { description: "È un limite di Discord. Mettilo nel testo del messaggio con il tasto «📎 Allega»." });
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setBusy(true);
    try {
      const url = await uploadMedia(file);
      if (video) { onVideo(url); toast.success("Video caricato", { description: "L'ho aggiunto al testo del messaggio: su Discord comparirà con il player." }); }
      else { onChange(url); toast.success(file.type === "image/gif" ? "GIF caricata" : "Immagine caricata"); }
    } catch (e) { toast.error(e.message); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  // si può anche trascinare il file sopra al campo
  const onDrop = (e) => { const f = e.dataTransfer?.files?.[0]; if (f) { e.preventDefault(); upload(f); } };

  return (
    <div className="space-y-1.5" onDragOver={(e) => e.preventDefault()} onDrop={onDrop}>
      <div className="flex gap-1.5">
        <div className="relative min-w-0 flex-1">
          <Input value={value || ""} onChange={(e) => onChange(e.target.value)} onBlur={(e) => resolve(e.target.value.trim())}
            onPaste={(e) => {
              const f = e.clipboardData.files?.[0];
              if (f) { e.preventDefault(); upload(f); return; }
              const t = e.clipboardData.getData("text").trim();
              if (PAGE_LINK.test(t)) { e.preventDefault(); onChange(t); resolve(t); }
            }}
            placeholder={placeholder} className={`${inputC} pr-8`} />
          {value && <button type="button" title="Togli" onClick={() => onChange("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#6b7185] hover:text-white"><X className="h-4 w-4" /></button>}
        </div>
        <input ref={fileRef} type="file" accept={onVideo ? `${IMAGE_TYPES},${VIDEO_TYPES}` : IMAGE_TYPES} className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
        <button type="button" disabled={busy} onClick={() => fileRef.current?.click()} title="Carica dal PC (puoi anche trascinare il file qui o incollarlo con Ctrl+V)"
          className="flex shrink-0 items-center gap-1.5 rounded-md border border-[#232838] bg-[#131620] px-2.5 text-xs font-semibold text-[#c7cbfb] hover:border-[#5865F2]/60 hover:text-white disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}{!compact && "Carica"}
        </button>
      </div>
      {value && (
        <div className="flex items-center gap-2">
          <img src={value} alt="" className="h-12 max-w-[110px] rounded border border-[#232838] object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          {/\.gif($|\?)/i.test(value) && <span className="flex items-center gap-1 rounded bg-[#5865F2]/20 px-1.5 py-0.5 text-[10px] font-bold text-[#c7cbfb]"><Film className="h-3 w-3" />GIF animata</span>}
        </div>
      )}
    </div>
  );
}

/** Tasto «📎 Allega» per i testi: carica immagine/GIF/video e inserisce il link nel testo (Discord lo mostra in anteprima). */
export function AttachButton({ onInsert, className = "" }) {
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const pick = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const url = await uploadMedia(file);
      onInsert(url);
      toast.success(file.type.startsWith("video/") ? "Video allegato" : "Immagine allegata", { description: "Su Discord comparirà sotto al testo." });
    } catch (e) { toast.error(e.message); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <>
      <input ref={ref} type="file" accept={`${IMAGE_TYPES},${VIDEO_TYPES}`} className="hidden" onChange={(e) => pick(e.target.files?.[0])} />
      <button type="button" disabled={busy} onMouseDown={(e) => e.preventDefault()} onClick={() => ref.current?.click()} title="Allega un'immagine, una GIF o un video (max 25 MB)"
        className={`flex h-7 items-center gap-1 rounded-md px-2 text-xs font-bold text-[#c7cbfb] transition-colors hover:bg-white/5 hover:text-white disabled:opacity-50 ${className}`}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}Allega
      </button>
    </>
  );
}
